import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { api } from '@starling/core/api/client';
import { ApiError } from '@starling/core/api/errors';
import { useAuthStore } from '@starling/core/auth/store';
import { ErrorAlert } from '@/components/ErrorAlert';
import { useCustomerQuery } from '@/customers/hooks';
import { useMessageStatusQuery, type Message } from '@/messages/hooks';
import { useOrderQuery } from '@/orders/hooks';
import { useTenantSettingsQuery, useTenantBlocked } from '@/settings/hooks';
import { BlockedActionTooltip } from '@/components/BlockedActionTooltip';
import { deriveChannelAvailability } from '@/messaging/channelAvailability';
import { useTemplatesQuery, type Template } from '@/templates/hooks';
import { templateDisplayName } from '@/templates/displayName';
import { errorCodeText } from '@/messages/errorCodeText';
import { useSuggestedTemplateQuery } from '@/orders/hooks';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface SendMessageDialogProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  orderId?: string;
  initialTemplateCode?: string;
  initialMode?: 'template' | 'free-text';
}

type Mode = 'template' | 'free-text';
type Locale = 'ru' | 'kk';

const POLLING_TIMEOUT_MS = 30_000;
const TERMINAL_STATUSES: ReadonlyArray<Message['status']> = [
  'sent',
  'delivered',
  'failed',
  'skipped',
];

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push((bytes[i] ?? 0).toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

function statusTagColor(status: Message['status']): string {
  switch (status) {
    case 'sent':
    case 'delivered':
      return 'success';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'warning';
    case 'sending':
      return 'processing';
    default:
      return 'default';
  }
}

// Простая клиентская подстановка переменных для предпросмотра. На сервере рендер
// делается через `str.format_map` (M2.4) — порядок аргументов один в один.
// Если переменная не предоставлена — оставляем плейсхолдер в превью, на сервере
// это поймает `missing_template_variables` (мы UI-стороной тоже проверяем,
// чтобы дать ранний фидбэк).
function renderPreview(body: string, vars: Record<string, string | undefined>): string {
  return body.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const v = vars[name];
    return v === undefined ? `{${name}}` : v;
  });
}

interface SendPayload {
  customer_id: string;
  order_id?: string | null;
  template_code?: string;
  body?: string;
  locale: Locale;
}

function useSendMessageMutation() {
  return useMutation({
    mutationKey: ['messages', 'send'],
    mutationFn: ({ payload, idempotencyKey }: { payload: SendPayload; idempotencyKey: string }) =>
      api.post<Message>('/api/v1/admin/messages', payload, {
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
  });
}

export function SendMessageDialog({
  open,
  onClose,
  customerId,
  orderId,
  initialTemplateCode,
  initialMode = 'template',
}: SendMessageDialogProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const { isBlocked } = useTenantBlocked();

  // Базовые данные.
  const customerQuery = useCustomerQuery(customerId);
  const orderQuery = useOrderQuery(orderId);
  const templatesQuery = useTemplatesQuery();
  const suggestedQuery = useSuggestedTemplateQuery(orderId);
  const tenantSettingsQuery = useTenantSettingsQuery();

  const customer = customerQuery.data ?? null;
  const order = orderQuery.data ?? null;
  const templates = useMemo(() => templatesQuery.data?.items ?? [], [templatesQuery.data]);

  // Местный стейт.
  const [mode, setMode] = useState<Mode>(initialMode);
  const [templateCode, setTemplateCode] = useState<string | undefined>(initialTemplateCode);
  const [locale, setLocale] = useState<Locale>('ru');
  const [freeText, setFreeText] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => generateUuid());
  const [messageId, setMessageId] = useState<string | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const timeoutHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sendMutation = useSendMessageMutation();
  const statusQuery = useMessageStatusQuery(messageId, !pollingTimedOut);

  const currentMessage = statusQuery.data ?? sendMutation.data ?? null;
  const isTerminal = useMemo<boolean>(() => {
    if (!currentMessage) return false;
    return TERMINAL_STATUSES.includes(currentMessage.status);
  }, [currentMessage]);

  // Если из родителя пришла suggested-template (через initialTemplateCode) —
  // используем её. Иначе при открытии для заказа — подставляем suggested из API,
  // если уже подгрузилась.
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setFreeText('');
    setIdempotencyKey(generateUuid());
    setMessageId(null);
    setPollingTimedOut(false);
    sendMutation.reset();
    if (timeoutHandleRef.current) {
      clearTimeout(timeoutHandleRef.current);
      timeoutHandleRef.current = null;
    }
    if (initialTemplateCode) {
      setTemplateCode(initialTemplateCode);
    } else if (suggestedQuery.data) {
      setTemplateCode(suggestedQuery.data.code);
    } else {
      setTemplateCode(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Подхватываем suggested-template после загрузки, если ничего ещё не выбрано.
  useEffect(() => {
    if (!open) return;
    if (!templateCode && !initialTemplateCode && suggestedQuery.data) {
      setTemplateCode(suggestedQuery.data.code);
    }
  }, [open, templateCode, initialTemplateCode, suggestedQuery.data]);

  // Таймер polling.
  useEffect(() => {
    if (!messageId) return;
    if (isTerminal) {
      if (timeoutHandleRef.current) {
        clearTimeout(timeoutHandleRef.current);
        timeoutHandleRef.current = null;
      }
      return;
    }
    if (timeoutHandleRef.current) return;
    timeoutHandleRef.current = setTimeout(() => {
      setPollingTimedOut(true);
    }, POLLING_TIMEOUT_MS);
    return () => {
      if (timeoutHandleRef.current) {
        clearTimeout(timeoutHandleRef.current);
        timeoutHandleRef.current = null;
      }
    };
  }, [messageId, isTerminal]);

  // На завершение — инвалидируем messages, чтобы журнал и список карточки
  // обновились без ручного refresh'а. Orders инвалидируем тоже — на детали
  // заказа список сообщений построен поверх messages.
  useEffect(() => {
    if (isTerminal) {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    }
  }, [isTerminal, qc]);

  const selectedTemplate: Template | null = useMemo(
    () => (templateCode ? (templates.find((tpl) => tpl.code === templateCode) ?? null) : null),
    [templateCode, templates],
  );

  // Контекст переменных для шаблона. `tenant_name` (M6, миграция 0016) резолвится
  // на сервере из `tenant.name` в `_derive_variables`; для превью и валидации берём
  // то же значение из tenant-settings, иначе UI ложно счёл бы его «не заданным».
  const variableContext = useMemo<Record<string, string | undefined>>(
    () => ({
      customer_name: customer?.name,
      order_number: order?.number,
      tenant_name: tenantSettingsQuery.data?.name,
    }),
    [customer, order, tenantSettingsQuery.data],
  );

  // Проверки гейтов перед submit.
  const consentBlocks =
    mode === 'template' &&
    selectedTemplate?.category === 'marketing' &&
    customer?.consent_marketing === false;

  const missingVariables = useMemo<string[]>(() => {
    if (mode !== 'template' || !selectedTemplate) return [];
    return selectedTemplate.variables.filter((v) => !variableContext[v]);
  }, [mode, selectedTemplate, variableContext]);

  const customerNotLinked = customer ? customer.linked_at === null : false;

  // M4.4 (#8): доступность каналов считаем ДО набора текста, чтобы не дать
  // пользователю упереться в 422 customer_not_linked post-factum. Логика —
  // общий хелпер (тот же в OrderDetailPage), см. deriveChannelAvailability.
  const tenantSettings = tenantSettingsQuery.data ?? null;
  const { smsAvailable, hasLiveChannel } = deriveChannelAvailability(customer, tenantSettings);
  // Guard от ложного «нет канала» при незагруженных данных: показываем ветку
  // только когда ОБЕ query успешно отдали данные.
  const channelsResolved = customerQuery.isSuccess && tenantSettingsQuery.isSuccess;
  const noChannelBlocked = channelsResolved && !hasLiveChannel;

  const previewBody = useMemo<string>(() => {
    if (mode === 'free-text') return freeText;
    if (!selectedTemplate) return '';
    const body = selectedTemplate.bodies[locale] ?? selectedTemplate.bodies['ru'] ?? '';
    return renderPreview(body, variableContext);
  }, [mode, freeText, selectedTemplate, locale, variableContext]);

  // M3.3+: customerNotLinked больше не блокирует submit — backend dispatch
  // выберет SMS-fallback при SMS_ENABLED=true. Если SMS отключён, бэк
  // отдаст 422 customer_not_linked, и пользователь увидит ошибку post-factum.
  // UI показывает info-Alert с подсказкой, но не disabled-ит кнопку.
  const canSubmit =
    !sendMutation.isPending &&
    !messageId &&
    !consentBlocks &&
    !noChannelBlocked &&
    !isBlocked &&
    (mode === 'free-text'
      ? freeText.trim().length > 0
      : Boolean(selectedTemplate) && missingVariables.length === 0);

  const handleClose = (): void => {
    if (timeoutHandleRef.current) {
      clearTimeout(timeoutHandleRef.current);
      timeoutHandleRef.current = null;
    }
    onClose();
  };

  const handleGoToSettings = (): void => {
    handleClose();
    navigate('/settings');
  };

  const handleSubmit = (): void => {
    if (!canSubmit || !customer) return;
    const payload: SendPayload =
      mode === 'free-text'
        ? {
            customer_id: customer.id,
            ...(orderId ? { order_id: orderId } : {}),
            body: freeText.trim(),
            locale: 'ru',
          }
        : {
            customer_id: customer.id,
            ...(orderId ? { order_id: orderId } : {}),
            template_code: templateCode!,
            locale,
          };

    sendMutation.mutate(
      { payload, idempotencyKey },
      {
        onSuccess: (msg) => {
          setMessageId(msg.id);
        },
      },
    );
  };

  const handleSendAnother = (): void => {
    setFreeText('');
    setIdempotencyKey(generateUuid());
    setMessageId(null);
    setPollingTimedOut(false);
    sendMutation.reset();
  };

  // Группировка шаблонов для Select.
  const templateOptions = useMemo(() => {
    const transactional = templates.filter((tpl) => tpl.category === 'transactional');
    const marketing = templates.filter((tpl) => tpl.category === 'marketing');
    const groups: { label: string; options: { value: string; label: string }[] }[] = [];
    if (transactional.length > 0) {
      groups.push({
        label: t('templates.category.transactional'),
        options: transactional.map((tpl) => ({
          value: tpl.code,
          label: templateDisplayName(t, tpl.code),
        })),
      });
    }
    if (marketing.length > 0) {
      groups.push({
        label: t('templates.category.marketing'),
        options: marketing.map((tpl) => ({
          value: tpl.code,
          label: templateDisplayName(t, tpl.code),
        })),
      });
    }
    return groups;
  }, [templates, t]);

  let errorNode: React.ReactNode = null;
  if (sendMutation.error instanceof ApiError) {
    const err = sendMutation.error;
    const key = `send_dialog.errors.${err.code}`;
    const translated = t(key, { defaultValue: '' });
    errorNode = (
      <Alert
        type="error"
        showIcon
        message={translated || err.message}
        description={err.code ? `Код: ${err.code}` : undefined}
      />
    );
  } else if (sendMutation.error) {
    errorNode = <ErrorAlert error={sendMutation.error} />;
  }

  const title = customer ? customer.name : t('send_dialog.title');
  const subtitle = order
    ? `${t('send_dialog.title')} · ${t('orders.table.number')}: ${order.number}`
    : t('send_dialog.title');

  return (
    <Modal
      title={
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {subtitle}
          </Text>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      width={720}
      footer={
        currentMessage && isTerminal ? (
          <Space>
            <Button onClick={handleClose}>{t('common.close')}</Button>
            <Button type="primary" onClick={handleSendAnother}>
              {t('send_dialog.send_again')}
            </Button>
          </Space>
        ) : currentMessage ? (
          <Button onClick={handleClose}>{t('common.close')}</Button>
        ) : noChannelBlocked ? (
          <Button onClick={handleClose}>{t('common.close')}</Button>
        ) : (
          <Space>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <BlockedActionTooltip>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={sendMutation.isPending}
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {t('send_dialog.send')}
              </Button>
            </BlockedActionTooltip>
          </Space>
        )
      }
    >
      {(customerQuery.isLoading || templatesQuery.isLoading || tenantSettingsQuery.isLoading) && (
        <Skeleton active paragraph={{ rows: 4 }} />
      )}

      {/* M4.4 (#8): нет живого канала — форму прячем, показываем центральный
          warning с подсказкой по роли. */}
      {noChannelBlocked && !currentMessage && (
        <Alert
          type="warning"
          showIcon
          data-testid="no-channel-alert"
          message={t('send_dialog.no_channel.title')}
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <span>
                {isAdmin ? t('send_dialog.no_channel.admin') : t('send_dialog.no_channel.employee')}
              </span>
              {isAdmin && (
                <Button type="link" style={{ padding: 0 }} onClick={handleGoToSettings}>
                  {t('send_dialog.no_channel.go_to_settings')}
                </Button>
              )}
            </Space>
          }
        />
      )}

      {/* info-Alert про SMS-fallback — только когда канал ЕСТЬ (unlinked, но SMS
          доступен). При noChannelBlocked показывается warning выше. */}
      {customerNotLinked && smsAvailable && !noChannelBlocked && !currentMessage && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('send_dialog.info.unlinked_fallback_sms')}
        />
      )}

      {!currentMessage && customer && !noChannelBlocked && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
            options={[
              { value: 'template', label: t('send_dialog.mode_template') },
              { value: 'free-text', label: t('send_dialog.mode_free_text') },
            ]}
          />

          {mode === 'template' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text
                  type="secondary"
                  style={{
                    display: 'block',
                    marginBottom: 6,
                    fontSize: 12,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('send_dialog.template_label')}
                </Text>
                <Select
                  value={templateCode}
                  onChange={(v) => setTemplateCode(v)}
                  placeholder={t('send_dialog.template_placeholder')}
                  options={templateOptions}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </div>

              <Radio.Group
                value={locale}
                onChange={(e) => setLocale(e.target.value as Locale)}
                options={[
                  { value: 'ru', label: t('send_dialog.locale_ru') },
                  { value: 'kk', label: t('send_dialog.locale_kk') },
                ]}
              />

              {selectedTemplate && (
                <div>
                  <Text
                    type="secondary"
                    style={{
                      display: 'block',
                      marginBottom: 6,
                      fontSize: 12,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('send_dialog.preview')}
                  </Text>
                  <div
                    style={{
                      padding: 14,
                      background: 'var(--mist)',
                      border: '1px solid var(--hairline)',
                      borderRadius: 8,
                      whiteSpace: 'pre-wrap',
                      fontSize: 13,
                      lineHeight: 1.6,
                    }}
                  >
                    {previewBody || <Text type="secondary">{t('send_dialog.preview_empty')}</Text>}
                  </div>
                  {selectedTemplate.variables.length > 0 && (
                    <Space wrap style={{ marginTop: 8 }}>
                      {selectedTemplate.variables.map((v) => (
                        <Tag
                          key={v}
                          color={missingVariables.includes(v) ? 'red' : 'cyan'}
                          className="sl-mono"
                        >
                          {`{${v}}`}
                        </Tag>
                      ))}
                    </Space>
                  )}
                </div>
              )}

              {consentBlocks && (
                <Alert
                  type="error"
                  showIcon
                  message={t('send_dialog.consent_blocked_title')}
                  description={t('send_dialog.consent_blocked_description')}
                />
              )}

              {missingVariables.length > 0 && !consentBlocks && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('send_dialog.missing_variables_title')}
                  description={t('send_dialog.missing_variables_description', {
                    vars: missingVariables.join(', '),
                  })}
                />
              )}
            </Space>
          )}

          {mode === 'free-text' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text
                  type="secondary"
                  style={{
                    display: 'block',
                    marginBottom: 6,
                    fontSize: 12,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('send_dialog.free_text_label')}
                </Text>
                <TextArea
                  rows={5}
                  maxLength={4096}
                  showCount
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  placeholder={t('send_dialog.body_placeholder')}
                  disabled={sendMutation.isPending}
                />
              </div>
              <Alert type="info" showIcon message={t('send_dialog.free_text_hint')} />
            </Space>
          )}

          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('send_dialog.idempotency_hint')}
          </Text>
        </Space>
      )}

      {currentMessage && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID:{' '}
            </Text>
            <Text code className="sl-mono">
              {currentMessage.id}
            </Text>
          </div>
          <div>
            <Tag
              color={statusTagColor(currentMessage.status)}
              style={{ fontSize: 14, padding: '4px 12px' }}
            >
              {t(`send_dialog.status.${currentMessage.status}`)}
            </Tag>
            {currentMessage.error_code && (
              <span style={{ marginLeft: 12, display: 'inline-flex', flexDirection: 'column' }}>
                <Text type="danger">
                  {errorCodeText(t, currentMessage.error_code) ?? currentMessage.error_code}
                </Text>
                <Text type="secondary" className="sl-mono" style={{ fontSize: 11 }}>
                  {currentMessage.error_code}
                </Text>
              </span>
            )}
          </div>
          {currentMessage.body && (
            <div
              style={{
                padding: 14,
                background: 'var(--mist)',
                border: '1px solid var(--hairline)',
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              {currentMessage.body}
            </div>
          )}
          {pollingTimedOut && !isTerminal && (
            <Alert type="warning" showIcon message={t('send_dialog.polling_timeout')} />
          )}
        </Space>
      )}

      {errorNode && <div style={{ marginTop: 12 }}>{errorNode}</div>}

      {!order && orderId && orderQuery.isError && (
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          {t('send_dialog.order_not_loaded')}
        </Paragraph>
      )}
    </Modal>
  );
}
